import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Post } from "@/types/content";
import { categories } from "@/mocks/categories";
import PostCard from "@/components/base/PostCard";
import HotList from "@/components/feature/HotList";

interface FeedWithSidebarProps {
  posts: Post[];
}

function catName(slug: string) {
  return categories.find((c) => c.slug === slug)?.name ?? "综合";
}

export default function FeedWithSidebar({ posts }: FeedWithSidebarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQ);
  const [activeCat, setActiveCat] = useState<string>("all");
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  useEffect(() => {
    setKeyword(searchParams.get("q") ?? "");
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    let list = posts.filter((p) => (activeCat === "all" ? true : p.categorySlug === activeCat));
    if (q) {
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return [...list].sort((a, b) => (sort === "hot" ? b.heat - a.heat : b.date.localeCompare(a.date)));
  }, [posts, activeCat, sort, keyword]);

  const applySearch = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const tabList = [{ slug: "all", name: "全部" }, ...categories.map((c) => ({ slug: c.slug, name: c.name }))];

  const editorPicks = posts.filter((p) => p.categorySlug === "picks").slice(0, 3);
  const allTags = Array.from(new Set(posts.flatMap((p) => p.tags))).slice(0, 14);

  return (
    <section id="feed" aria-labelledby="feed-title" className="w-full px-4 md:px-6 pb-12 md:pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* 主信息流 */}
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-5">
            <h2 id="feed-title" className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
              最新投稿
            </h2>
            <span className="flex-1 h-px rule-dashed" />
          </div>

          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-wrap items-center gap-2">
              {tabList.map((tab) => (
                <button
                  key={tab.slug}
                  type="button"
                  onClick={() => setActiveCat(tab.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCat === tab.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
              <div className="relative flex-1 sm:max-w-xs">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                  aria-hidden="true"
                />
                <label htmlFor="feed-search" className="sr-only">
                  在投稿中搜索
                </label>
                <input
                  id="feed-search"
                  value={keyword}
                  onChange={(e) => applySearch(e.target.value)}
                  placeholder="在投稿中搜索关键词"
                  className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div className="flex items-center gap-1 bg-background-100 p-1 rounded-full w-max">
                {[
                  { key: "latest" as const, label: "最新" },
                  { key: "hot" as const, label: "最热" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setSort(item.key)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sort === item.key ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="mb-4 text-xs text-foreground-500">
            共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 条相关条目
            {keyword.trim() && <>，关键词「{keyword.trim()}」</>}
          </p>

          {filtered.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-background-300 rounded-lg">
              <i className="ri-inbox-2-line text-4xl text-background-400" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">没有找到匹配的条目，换个关键词试试。</p>
              <button
                type="button"
                onClick={() => {
                  applySearch("");
                  setActiveCat("all");
                }}
                className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {filtered.map((post) => (
                <PostCard key={post.id} post={post} categoryName={catName(post.categorySlug)} />
              ))}
            </div>
          )}
        </div>

        {/* 侧边栏 */}
        <aside className="lg:col-span-4 flex flex-col gap-6">
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <HotList posts={[...posts].sort((a, b) => b.heat - a.heat)} limit={8} />

            <section aria-labelledby="editor-pick-title" className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h3 id="editor-pick-title" className="flex items-center gap-2 text-sm font-bold text-foreground-900 mb-4">
                <i className="ri-bookmark-3-line text-secondary-600" aria-hidden="true" />
                编辑推荐
              </h3>
              <ul className="space-y-4">
                {editorPicks.map((post) => (
                  <li key={post.id}>
                    <Link to={`/post/${post.slug}`} className="group flex gap-3">
                      <span className="w-1.5 rounded-full bg-accent-400 group-hover:bg-primary-500 transition-colors shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {post.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">{post.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="tag-cloud-title" className="bg-secondary-50 border border-secondary-100 rounded-lg p-4">
              <h3 id="tag-cloud-title" className="flex items-center gap-2 text-sm font-bold text-secondary-900 mb-3">
                <i className="ri-price-tag-3-line" aria-hidden="true" />
                热门标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => applySearch(tag)}
                    className="px-2.5 py-1 rounded-md bg-background-50 border border-secondary-200 text-xs text-secondary-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </section>
          </div>
        </aside>
      </div>
    </section>
  );
}